import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { useAction, useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { ShieldCheck, Eye, EyeOff, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const activateSearchSchema = z.object({
  token: z.string().optional(),
});

export const Route = createFileRoute("/_public/activate")({
  validateSearch: activateSearchSchema,
  component: ActivatePage,
});

// Pantalla de activación: el usuario llega por un link /activate?token=... que
// le pasó su admin (sin email). Valida el token, fija la contraseña y entra.
function ActivatePage() {
  const navigate = useNavigate();
  const { token } = Route.useSearch();
  const activateAccount = useAction(
    api.system.activation.actions.activateAccount,
  );

  const status = useQuery(
    api.system.activation.queries.getActivationStatus,
    token ? { token } : "skip",
  );

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const invalid = !token || (status !== undefined && status.valid === false);

  if (token && status === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (invalid) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-accent/50 p-4">
        <div className="w-full max-w-md space-y-4 rounded-lg border bg-background p-6 text-center shadow-sm">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-muted">
            <AlertTriangle className="h-6 w-6 text-amber-500" />
          </div>
          <h1 className="text-lg font-semibold">Link no válido</h1>
          <p className="text-sm text-muted-foreground">
            Este link de activación no existe, ya fue usado o expiró. Pide a tu
            administrador que te genere uno nuevo.
          </p>
          <Button
            variant="outline"
            className="w-full"
            onClick={() => navigate({ to: "/auth/sign-in" })}
          >
            Ir a iniciar sesión
          </Button>
        </div>
      </div>
    );
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("La contraseña debe tener al menos 8 caracteres");
      return;
    }
    if (password !== confirm) {
      toast.error("Las contraseñas no coinciden");
      return;
    }

    setSubmitting(true);
    try {
      await activateAccount({ token: token!, password });
      toast.success("Cuenta activada. Ya puedes iniciar sesión.");
      navigate({
        to: "/auth/sign-in",
        search: { message: "account-configured" },
        replace: true,
      });
    } catch (err) {
      console.error("Error activando la cuenta:", err);
      toast.error(
        "No se pudo activar la cuenta. El link puede haber expirado.",
      );
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-accent/50 p-4">
      <div className="w-full max-w-md space-y-5 rounded-lg border bg-background p-6 shadow-sm">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-primary" />
          <h1 className="text-lg font-semibold">Activa tu cuenta</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          {status?.valid && status.email ? (
            <>
              Crea tu contraseña para{" "}
              <strong className="text-foreground">{status.email}</strong>.
            </>
          ) : (
            "Crea tu contraseña para acceder."
          )}
        </p>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="password">Contraseña</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mínimo 8 caracteres"
                disabled={submitting}
              />
              <button
                type="button"
                className="absolute border-none right-0 bg-transparent top-0 h-full px-3 py-2 hover:bg-transparent"
                onClick={() => setShowPassword((v) => !v)}
                disabled={submitting}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Eye className="h-4 w-4 text-muted-foreground" />
                )}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm">Confirmar contraseña</Label>
            <Input
              id="confirm"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Repite tu contraseña"
              disabled={submitting}
            />
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={submitting || !password || !confirm}
          >
            {submitting ? "Activando…" : "Activar cuenta"}
          </Button>
        </form>
      </div>
    </div>
  );
}
